import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCcw, ShieldCheck } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

interface Signal {
  id: string;
  shopperId: string;
  abandoned24h: number;
  flagged: boolean;
  lastSignalAt?: { toMillis?: () => number };
  events?: Array<{ orderId: string; businessId: string; variantIds: string[]; trigger?: string }>;
}

export function AdminAbandonment() {
  const [signals, setSignals] = useState<Signal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'listAbandonmentSignals');
      const res: any = await fn({});
      setSignals(res.data.signals || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load signals');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const clear = async (shopperId: string) => {
    setBusy(shopperId);
    try {
      const fn = httpsCallable(functions, 'clearAbandonmentReview');
      await fn({ shopperId });
      await load();
    } catch (err: any) {
      setError(err?.message || 'Clear failed');
    }
    setBusy(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-heading">Checkout abandonment</h1>
          <p className="text-sm text-body">
            Flagged shoppers only — no automated punishment. Review the trail and clear after investigating.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load}>
          <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {loading && <Loader2 className="h-5 w-5 animate-spin" />}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {signals.map((s) => (
        <Card key={s.id}>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Shopper {s.shopperId}
                </p>
                <p className="text-xs text-body">{s.abandoned24h} abandoned checkout(s) in 24h</p>
              </div>
              <Button size="sm" disabled={busy === s.shopperId} onClick={() => clear(s.shopperId)}>
                <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                Clear flag
              </Button>
            </div>
            <ul className="text-xs text-body space-y-1">
              {(s.events || []).slice(-8).map((e) => (
                <li key={e.orderId}>
                  {e.trigger || 'abandoned'} · order {e.orderId} · {e.variantIds?.length || 0} variant(s)
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ))}

      {!loading && signals.length === 0 && (
        <p className="text-sm text-body">No flagged shoppers right now.</p>
      )}
    </div>
  );
}
